// Hand-maintained — override generation
// Regenerate with: mix update.sdks (manually merge new namespaces after regeneration)

import { BaseClient, type BaseClientConfig } from "./base-client";
import { RequestBuilder } from "./request-builder";
import { createAgentsNamespace } from "./namespaces/agents";
import { createAccountsNamespace } from "./namespaces/accounts";
import { createApplicationsNamespace } from "./namespaces/applications";
import { createCapabilitiesNamespace } from "./namespaces/capabilities";
import { createContentNamespace } from "./namespaces/content";
import { createApiKeysNamespace } from "./namespaces/apiKeys";
import { createExecutionsNamespace } from "./namespaces/executions";
import { createStorageNamespace } from "./namespaces/storage";
import { createUsersNamespace } from "./namespaces/users";
import { createVoiceNamespace } from "./namespaces/voice";
import { createAuditNamespace } from "./namespaces/audit";
import { createWebhooksNamespace } from "./namespaces/webhooks-ns";
import { createCampaignsNamespace } from "./namespaces/campaigns";
import { createEmailNamespace } from "./namespaces/email";
import { createBillingNamespace } from "./namespaces/billing";
import { createPortalNamespace } from "./namespaces/portal";
import { createSocialNamespace } from "./namespaces/social";
import { createConnectorsNamespace } from "./namespaces/connectors";
import { createCrawlerNamespace } from "./namespaces/crawler";
import { createExtractionNamespace } from "./namespaces/extraction";
import { createSchedulingNamespace } from "./namespaces/scheduling";
import { createCrmNamespace } from "./namespaces/crm";
import { createSupportNamespace } from "./namespaces/support";
import { createCasesNamespace } from "./namespaces/cases";
import { createContractsNamespace } from "./namespaces/contracts";
import { createMeetingsNamespace } from "./namespaces/meetings";
import { createReviewsNamespace } from "./namespaces/reviews";
import { createModelsNamespace } from "./namespaces/models";
import { createAiNamespace } from "./namespaces/ai";
import { createCrmClustersNamespace } from "./namespaces/crm-clusters";
import { createComplianceNamespace } from "./namespaces/compliance";
import { createPipelineNodeExecutionsNamespace } from "./namespaces/pipeline-node-executions";
import { createPipelineNodesNamespace } from "./namespaces/pipeline-nodes";
import { createPipelinesNamespace } from "./namespaces/pipelines";
import { createPipelineExecutionsNamespace } from "./namespaces/pipeline-executions";
import { createTenantsNamespace } from "./namespaces/tenants";
import { createAccessGrantsNamespace } from "./namespaces/access-grants";
import { createRolesNamespace } from "./namespaces/roles";
import { createPermissionsNamespace } from "./namespaces/permissions";
import { createOwnershipTransfersNamespace } from "./namespaces/ownership-transfers";
import { createThreadsNamespace } from "./namespaces/threads";
import { createClinicalNamespace } from "./namespaces/clinical";
import { createWorkspacesNamespace } from "./namespaces/workspaces";
import { createInvitationsNamespace } from "./namespaces/invitations";
import { createBrandIdentitiesNamespace } from "./namespaces/brand-identities";
import { createPlatformTonesNamespace } from "./namespaces/platform-tones";
import { createClaimedDomainsNamespace } from "./namespaces/claimed-domains";
import { createSystemMessagesNamespace } from "./namespaces/system-messages";
import { createChannelsNamespace } from "./namespaces/channels";
import { createImportsNamespace } from "./namespaces/imports";
import { createDocumentsNamespace } from "./namespaces/documents";
import { createInvoicesNamespace } from "./namespaces/invoices";
import { createRecipesNamespace } from "./namespaces/recipes";
import { createPreferencesNamespace } from "./namespaces/preferences";
import { createRegistryNamespace } from "./namespaces/registry";
import { createUserProfilesNamespace } from "./namespaces/userProfiles";
import { createScopesNamespace } from "./namespaces/scopes";
import { createThemesNamespace } from "./namespaces/themes";
import { createFormsNamespace } from "./namespaces/forms";

export class GptAdmin extends BaseClient {
  /** Agent management, versioning, training, and field templates */
  public readonly agents;

  /** ISV application management — create, configure, and manage platform applications */
  public readonly applications;

  /** Billing account management */
  public readonly accounts;

  /** Platform capability catalog (admin view — full catalog, no app annotations) */
  public readonly capabilities;

  /** API key management */
  public readonly apiKeys;

  /** Agent execution management and streaming */
  public readonly executions;

  /** Storage operations and bucket management */
  public readonly storage;

  /** User management across all tenants */
  public readonly users;

  /** Voice session, recording, and transcription management */
  public readonly voice;

  /** Compliance audit log access — paginated listing, activity feeds, aggregates, and bulk exports */
  public readonly audit;

  /** Webhook configuration and delivery management */
  public readonly webhooks;

  /** AI content generation — text, images, tone rewriting, hashtags, SEO */
  public readonly content;

  /** Campaign management across all workspaces */
  public readonly campaigns;

  /** Outbound email and sender profile management across all workspaces */
  public readonly email;

  /** Tenant wallet management — credit adjustments, plan changes, usage history */
  public readonly billing;

  /** Portal management: invitations, change request review */
  public readonly portal;

  /** Social media accounts, posts, metrics, and AI-powered campaigns */
  public readonly social;

  /** Connector management: instances, credentials, OAuth configs, ISV tools */
  public readonly connectors;

  /** Web crawling, content extraction, and site configuration management */
  public readonly crawler;

  /** Scheduling: locations, event types, events, participants, bookings, availability, reminders, calendar syncs */
  public readonly scheduling;

  /** CRM management — contacts, companies, deals, pipelines, ISV entity type and field configuration */
  public readonly crm;

  /** Extraction pipeline: documents, results, batches, exports, workflows, analysis */
  public readonly extraction;

  /** Support domain: tickets, queues, SLA policies, routing, AI config, canned responses */
  public readonly support;

  /** Cases domain: operational case aggregate, state machine workflow, decisions, links */
  public readonly cases;

  /** Contracts domain: AI contract lifecycle, clauses, renewal alerts (capability: contract_tracking) */
  public readonly contracts;

  /** Meetings domain: AI meeting intelligence — meetings, transcripts, action items (capability: meeting_intelligence) */
  public readonly meetings;

  /** Reviews domain: human review queues, memberships, and lifecycle transitions */
  public readonly reviews;

  /** Model catalog — available models with tier classification */
  public readonly models;

  /** AI/LLM management — provider configs, analytics, semantic cache */
  public readonly ai;

  /** CRM cluster discovery — AI-labeled relationship clusters via community detection */
  public readonly crmClusters;

  /** Compliance scanning — PII/PHI detection and redaction (HIPAA Safe Harbor) */
  public readonly compliance;

  /** Agent pipeline management — create, configure, and trigger multi-node agent pipelines */
  public readonly pipelines;

  /** Pipeline execution management — HITL checkpoint lifecycle (approve, reject, edit, refine) */
  public readonly pipelineExecutions;

  /** Pipeline node management — create, update, delete, and query individual pipeline nodes */
  public readonly pipelineNodes;

  /** Pipeline node execution tracking — per-node execution status and output data */
  public readonly pipelineNodeExecutions;

  /** Tenant lifecycle management — suspend, unsuspend, cancel */
  public readonly tenants;

  /** ReBAC access grant management — create, list, update, and revoke grants */
  public readonly accessGrants;

  /** Role management — named permission bundles for workspace access control */
  public readonly roles;

  /** Permission registry — platform permissions, presets, and metadata */
  public readonly permissions;

  /** Ownership transfer audit log — read-only workspace-scoped history */
  public readonly ownershipTransfers;

  /** Chat thread management — cross-workspace thread inspection, moderation, and lifecycle */
  public readonly threads;

  /** Clinical analytics — ISV-level overview and pipeline metrics */
  public readonly clinical;

  /** Workspace management — create, configure, allocate credits, storage settings */
  public readonly workspaces;

  /** Invitation management — create, accept, decline, revoke, resend */
  public readonly invitations;

  /** Brand identity management — create, update, set/unset default, per-tenant/workspace */
  public readonly brandIdentities;

  /** Platform tone management — create, update, delete, per-brand listing */
  public readonly platformTones;

  /** Claimed domain management — enterprise DNS claiming, verify/revoke lifecycle */
  public readonly claimedDomains;

  /** Platform-wide system messages — ToS, privacy policy, welcome, announcements */
  public readonly systemMessages;

  /** HTML→PDF generation and report delivery */
  public readonly documents;

  /** Scoped channel tokens for WebSocket authentication */
  public readonly channels;

  /** Unified import hub — CSV/JSON batch imports across Clinical, CRM, and Catalog */
  public readonly imports;

  /** Bidirectional invoicing — invoices, lines, payments, templates, rules, recurring schedules, contacts */
  public readonly invoices;

  /** Recipe management, meal planning, shopping lists, nutrition tracking, and AI enrichment */
  public readonly recipes;

  /** Registry management — catalogs, items, versions, publishing */
  public readonly registry;

  /** User-scoped, workspace-isolated preference storage */
  public readonly preferences;

  /** User profile management — CRUD, ToS acceptance, welcome/announcement dismissals */
  public readonly userProfiles;

  /** Available API key scopes grouped by surface and namespace */
  public readonly scopes;

  /** Theme management — CRUD, registry import, and WCAG contrast reporting */
  public readonly themes;

  /** AI form generation (Slice 5) — async LLM-driven form authoring */
  public readonly forms;

  constructor(config?: BaseClientConfig) {
    super(config);
    const rb = new RequestBuilder(
      this.clientInstance,
      () => this.getHeaders(),
      <T>(d: unknown) => this.unwrap<T>(d),
      <T>(fn: () => Promise<T>) => this.requestWithRetry(fn),
      () => this.config.baseUrl,
    );

    this.agents = createAgentsNamespace(rb);
    this.applications = createApplicationsNamespace(rb);
    this.accounts = createAccountsNamespace(rb);
    this.capabilities = createCapabilitiesNamespace(rb);
    this.apiKeys = createApiKeysNamespace(rb);
    this.extraction = createExtractionNamespace(rb);
    this.documents = createDocumentsNamespace(rb);
    this.executions = createExecutionsNamespace(rb);
    this.storage = createStorageNamespace(rb);
    this.users = createUsersNamespace(rb);
    this.voice = createVoiceNamespace(rb);
    this.audit = createAuditNamespace(rb);
    this.webhooks = createWebhooksNamespace(rb);
    this.content = createContentNamespace(rb);
    this.campaigns = createCampaignsNamespace(rb);
    this.email = createEmailNamespace(rb);
    this.billing = createBillingNamespace(rb);
    this.portal = createPortalNamespace(rb);
    this.social = createSocialNamespace(rb);
    this.connectors = createConnectorsNamespace(rb);
    this.crawler = createCrawlerNamespace(rb);
    this.scheduling = createSchedulingNamespace(rb);
    this.crm = createCrmNamespace(rb);
    this.support = createSupportNamespace(rb);
    this.cases = createCasesNamespace(rb);
    this.contracts = createContractsNamespace(rb);
    this.meetings = createMeetingsNamespace(rb);
    this.reviews = createReviewsNamespace(rb);
    this.models = createModelsNamespace(rb);
    this.ai = createAiNamespace(rb);
    this.crmClusters = createCrmClustersNamespace(rb);
    this.compliance = createComplianceNamespace(rb);
    this.pipelines = createPipelinesNamespace(rb);
    this.pipelineExecutions = createPipelineExecutionsNamespace(rb);
    this.pipelineNodes = createPipelineNodesNamespace(rb);
    this.pipelineNodeExecutions = createPipelineNodeExecutionsNamespace(rb);
    this.tenants = createTenantsNamespace(rb);
    this.accessGrants = createAccessGrantsNamespace(rb);
    this.roles = createRolesNamespace(rb);
    this.permissions = createPermissionsNamespace(rb);
    this.ownershipTransfers = createOwnershipTransfersNamespace(rb);
    this.threads = createThreadsNamespace(rb);
    this.clinical = createClinicalNamespace(rb);
    this.workspaces = createWorkspacesNamespace(rb);
    this.invitations = createInvitationsNamespace(rb);
    this.brandIdentities = createBrandIdentitiesNamespace(rb);
    this.platformTones = createPlatformTonesNamespace(rb);
    this.claimedDomains = createClaimedDomainsNamespace(rb);
    this.systemMessages = createSystemMessagesNamespace(rb);
    this.channels = createChannelsNamespace(rb);
    this.imports = createImportsNamespace(rb);
    this.invoices = createInvoicesNamespace(rb);
    this.recipes = createRecipesNamespace(rb);
    this.registry = createRegistryNamespace(rb);
    this.preferences = createPreferencesNamespace(rb);
    this.userProfiles = createUserProfilesNamespace(rb);
    this.scopes = createScopesNamespace(rb);
    this.themes = createThemesNamespace(rb);
    this.forms = createFormsNamespace(rb);
  }
}
